/**
 * DocumentModal — Modal para crear y editar documentos de venta (VEN-03)
 * Formulario vertical con secciones: Encabezado, Receptor, Líneas, Referencias, Personalizados.
 * - Más ancho (max-w-4xl)
 * - Controles compactos
 * - Descripción por línea colapsable
 * - Auto-fill de datos al ingresar un SKU existente
 */
import { todayISO } from '../../../lib/utils'
import { useEffect, useRef, useState } from 'react'
import { formatRut } from '@/lib/utils'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import DatePicker from '@/components/DatePicker'
import { X, Loader2, Plus, Trash2, ChevronDown, ChevronRight, Building2 } from 'lucide-react'
import { Autocomplete } from '@/components/Autocomplete'
import type { AutocompleteItem } from '@/components/Autocomplete'
import { NumericInput } from '@/components/NumericInput'
import { useTenantLocale, formatMoneyForTenant, useTenantCurrency, currencyDecimals } from '@/lib/locale'
import { totalesDelDocumento, subtotalDeLinea, precioEnMonedaBase } from '../lib/documentTotals'
import { useTenantTax } from '@/modules/config/hooks/useConfig'
import type {
  SaleDocument,
  SaleDocumentLine,
  CustomerListItem,
  ProductListItem,
  DocumentReferencia,
  DocumentPersonalizado,
  PriceCurrency,
} from '@/modules/sales/hooks/useSales'
import { ProductAutocomplete } from './ProductAutocomplete'
import type { GlobalCustomField, CurrencyRateInfo } from '@/modules/config/hooks/useConfig'
import { CostCenterAllocator } from '@/modules/accounting/components/CostCenterAllocator'
import { FolioStatusNotice } from '@/modules/config/components/FolioStatusNotice'
import type { CostCenterAllocation } from '@/modules/accounting/components/CostCenterAllocator'
import { useDTETypes } from '@/modules/config/hooks/useConfig'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { Modal } from '@/components/ds/Modal'

interface GlobalCampoValue {
  id: number
  nombre: string
  tipo: 'string' | 'numero' | 'fecha' | 'select' | 'multiselect' | 'boolean' | 'entity_link'
  is_required: boolean
  valor: string
  opciones?: string[]
}

interface DocumentFormData {
  customer: string
  document_type: string
  issue_date: string
  payment_method: 'contado' | 'credito'
  payment_term_days: number
  notes: string
  lines: SaleDocumentLine[]
  referencias: DocumentReferencia[]
  global_campos: GlobalCampoValue[]
  personalizados: DocumentPersonalizado[]
}

interface DocumentModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: DocumentFormData) => void
  isPending: boolean
  initial?: Partial<SaleDocument> | null
  customers: CustomerListItem[]
  currencyRates?: Record<string, CurrencyRateInfo>
  globalFields?: GlobalCustomField[]
}

// Compact styles
const inputCls =
  'w-full px-2.5 py-2 text-sm sm:py-1.5 sm:text-xs border border-input-border rounded-md bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200/30 focus:border-primary-200 transition-all placeholder:text-content-subtle'
const labelCls = 'block text-xs font-medium text-content-muted mb-0.5'
const errorCls = 'mt-0.5 text-xs text-danger-solid'
const sectionTitleCls = 'text-xs font-semibold text-content-subtle uppercase tracking-wider mb-2'

const DOCUMENT_TYPES_FALLBACK = [
  { value: 'FE', label: 'Factura Electrónica' },
  { value: 'BE', label: 'Boleta Electrónica' },
  { value: 'NC', label: 'Nota de Crédito' },
  { value: 'ND', label: 'Nota de Débito' },
  { value: 'FC', label: 'Factura de Compra' },
]

const UNITS = ['UN', 'KG', 'MT', 'LT', 'HR', 'DY', 'MO', 'BX', 'PK', 'OT']

const COD_REF_OPTIONS = [
  { value: '', label: '— Sin código —' },
  { value: '1', label: '1 — Anula documento' },
  { value: '2', label: '2 — Corrige texto' },
  { value: '3', label: '3 — Corrige monto' },
]

const EMPTY_LINE: SaleDocumentLine = {
  sku: '',
  name: '',
  description: '',
  unit: 'UN',
  quantity: '1',
  unit_price: '0',
  price_currency: '',
  discount_pct: '0',
  is_tax_exempt: false,
  tax_data: {},
}

const EMPTY_REFERENCIA: DocumentReferencia = {
  tipo_doc: '',
  folio_ref: '',
  fecha_ref: '',
  cod_ref: '',
  razon_ref: '',
}

const EMPTY_PERSONALIZADO: DocumentPersonalizado = {
  tipo: 'string',
  nombre: '',
  valor: '',
}

interface ReceptorInfo {
  tax_id: string
  legal_name: string
  giro: string
  address_street: string
  address_commune: string
  address_city: string
}

const EMPTY_RECEPTOR: ReceptorInfo = {
  tax_id: '',
  legal_name: '',
  giro: '',
  address_street: '',
  address_commune: '',
  address_city: '',
}

const formatCLP = (val: number) =>
  formatMoneyForTenant(val)

export function DocumentModal({
  open,
  onClose,
  onSubmit,
  isPending,
  initial,
  customers,
  globalFields = [],
  currencyRates,
}: DocumentModalProps) {
  // El impuesto sale de Configuración -> Localización. Antes llegaba por prop
  // con `19` de default, así que cada llamador repetía la misma resolución y el
  // nombre quedaba fijo en «IVA».
  const tax = useTenantTax()
  const taxRate = tax.rate
  const today = todayISO()
  const { data: dteTypesData } = useDTETypes()
  const DOCUMENT_TYPES = dteTypesData
    ? dteTypesData.filter(t => t.is_active).map(t => ({ value: t.internal_code, label: t.display_name }))
    : DOCUMENT_TYPES_FALLBACK
  const [receptorDisplay, setReceptorDisplay] = useState<ReceptorInfo>(EMPTY_RECEPTOR)
  const locale = useTenantLocale()
  const baseCurrency = useTenantCurrency()
  // Track which lines have description/CC expanded (by field.id)
  const [expandedDesc, setExpandedDesc] = useState<Set<string>>(new Set())
  const [expandedCC, setExpandedCC] = useState<Set<string>>(new Set())

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<DocumentFormData>({
    defaultValues: {
      customer: '',
      document_type: 'FE',
      issue_date: today,
      payment_method: 'contado' as const,
      payment_term_days: 0,
      notes: '',
      lines: [{ ...EMPTY_LINE }],
      referencias: [],
      global_campos: [],
      personalizados: [],
    },
  })

  const {
    fields: lineFields,
    append: appendLine,
    remove: removeLine,
  } = useFieldArray({ control, name: 'lines' })

  const {
    fields: refFields,
    append: appendRef,
    remove: removeRef,
  } = useFieldArray({ control, name: 'referencias' })

  const {
    fields: globalCampoFields,
    remove: removeGlobalCampo,
  } = useFieldArray({ control, name: 'global_campos' })

  const {
    fields: persFields,
    append: appendPers,
    remove: removePers,
  } = useFieldArray({ control, name: 'personalizados' })

  // Inicializa el form UNA sola vez por apertura (clave = documento en edición o
  // "nuevo"). Sin este guard, el efecto se re-ejecutaba cada vez que `globalFields`
  // cambiaba de referencia (refetch de TanStack Query por window-focus/staleTime/
  // invalidación) y `reset()` botaba lo que el usuario estaba editando. Bug
  // reportado (Belén, 2026-07-14): al ajustar la razón/código de una referencia de
  // NC, volvían al default "Anula / cod_ref=1" — porque el refetch de campos
  // personalizados reseteaba el form al `initial` (que trae la referencia default).
  const initFormKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!open) {
      initFormKeyRef.current = null
      return
    }
    const key = (initial as any)?.id ?? '__new__'
    if (initFormKeyRef.current === key) return
    initFormKeyRef.current = key

    setExpandedDesc(new Set())
    setExpandedCC(new Set())

    // Build global_campos values from active global fields
    const activeGlobals = globalFields
      .filter(f => f.is_active)
      .sort((a, b) => a.order - b.order)

    const docPersonalizados: DocumentPersonalizado[] = initial?.personalizados || []
    const globalNombres = new Set(activeGlobals.map(f => f.nombre))

    const globalCampoValues: GlobalCampoValue[] = activeGlobals.map(f => {
      const existing = docPersonalizados.find(p => p.nombre === f.nombre)
      return {
        id: f.id,
        nombre: f.nombre,
        tipo: f.tipo,
        is_required: f.is_required,
        valor: existing?.valor ?? f.default_value,
        opciones: f.opciones || [],
      }
    })

    // Ad-hoc personalizados = those not matching any global field name
    const adHocPersonalizados = docPersonalizados.filter(p => !globalNombres.has(p.nombre))

    // When editing, restore original foreign currency prices
    const editLines =
      initial?.lines && initial.lines.length > 0
        ? initial.lines.map(line => ({
            ...line,
            unit_price:
              line.unit_price_original && line.price_currency && line.price_currency !== baseCurrency
                ? line.unit_price_original
                : line.unit_price,
          }))
        : [{ ...EMPTY_LINE }]

    reset({
      customer: initial?.customer || '',
      document_type: initial?.document_type || 'FE',
      issue_date: initial?.issue_date || today,
      payment_method: (initial as any)?.payment_method || 'contado',
      payment_term_days: (initial as any)?.payment_term_days || 0,
      notes: initial?.notes || '',
      lines: editLines,
      referencias: initial?.referencias || [],
      global_campos: globalCampoValues,
      personalizados: adHocPersonalizados,
    })
  }, [open, initial, reset, today, globalFields])

  // Si los campos personalizados globales llegan/actualizan DESPUÉS de abrir el
  // modal (query async), sincroniza SOLO `global_campos` sin tocar el resto del
  // form (referencias/líneas/notas). Preserva los valores ya tipeados por el
  // usuario (merge por nombre) → no reintroduce el clobber que arreglamos arriba.
  useEffect(() => {
    if (!open) return
    const activeGlobals = globalFields
      .filter(f => f.is_active)
      .sort((a, b) => a.order - b.order)
    const current = watch('global_campos') || []
    const currentByName = new Map(current.map(c => [c.nombre, c]))
    const sameSet =
      current.length === activeGlobals.length &&
      activeGlobals.every(f => currentByName.has(f.nombre))
    if (sameSet) return
    const docPersonalizados: DocumentPersonalizado[] = initial?.personalizados || []
    const merged: GlobalCampoValue[] = activeGlobals.map(f => {
      const existing = currentByName.get(f.nombre)
      const fromDoc = docPersonalizados.find(p => p.nombre === f.nombre)
      return {
        id: f.id,
        nombre: f.nombre,
        tipo: f.tipo,
        is_required: f.is_required,
        valor: existing?.valor ?? fromDoc?.valor ?? f.default_value,
        opciones: f.opciones || [],
      }
    })
    setValue('global_campos', merged)
  }, [open, globalFields, initial, setValue, watch])

  // Auto-fill receptor cuando cambia el cliente seleccionado
  const watchedCustomerId = watch('customer')
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  useEffect(() => {
    const found = customers.find(c => c.id === watchedCustomerId)
    if (found) {
      setReceptorDisplay({
        tax_id: found.tax_id || '',
        legal_name: found.legal_name || '',
        giro: found.giro || '',
        address_street: found.address_street || '',
        address_commune: found.address_commune || '',
        address_city: found.address_city || '',
      })
    } else {
      setReceptorDisplay(EMPTY_RECEPTOR)
    }
  }, [watchedCustomerId, customers])

  // Calcular totales en tiempo real con desglose IVA/Exento
  const watchedLines = watch('lines')
  const watchedDocType = watch('document_type')
  const watchedPaymentMethod = watch('payment_method')
  const watchedPaymentTermDays = watch('payment_term_days')
  const watchedIssueDate = watch('issue_date')

  // Auto-fill payment terms from customer
  useEffect(() => {
    const found = customers.find(c => c.id === watchedCustomerId)
    if (found && !initial?.id) {
      const cust = found as any
      if (cust.payment_term_days && cust.payment_term_days > 0) {
        setValue('payment_method', 'credito')
        setValue('payment_term_days', cust.payment_term_days)
      } else {
        setValue('payment_method', 'contado')
        setValue('payment_term_days', 0)
      }
    }
  }, [watchedCustomerId, customers, initial?.id, setValue])

  // Tasas bloqueadas del documento original cuando estamos creando un ajuste
  // (NC/ND) sobre un DTE en moneda extranjera. Reproducimos los mismos CLP que
  // aceptó el SII en vez de recotizar a la tasa vigente.
  const lockedRates = (initial as any)?.exchange_rate_data as
    | Record<string, string | number>
    | undefined

  // Helper: obtener tasa para una moneda
  const getRate = (currency: string): number | null => {
    if (!currency || currency === baseCurrency) return null
    if (lockedRates && lockedRates[currency] != null) {
      const v = parseFloat(String(lockedRates[currency]))
      if (!Number.isNaN(v)) return v
    }
    if (!currencyRates) return null
    const info = currencyRates[currency]
    return info ? parseFloat(info.rate) : null
  }

  const foreignCurrency = watchedLines
    .map(l => (l as any).price_currency)
    .find(c => c && c !== baseCurrency) || null

  // Los totales salen de `documentTotals`, que replica el orden de operaciones
  // del backend y redondea el subtotal POR LÍNEA. Sumar los subtotales sin
  // redondear daba un total distinto del guardado apenas un descuento producía
  // medios pesos.
  const opcionesDeTotales = { baseCurrency, taxRate, getRate }
  const { montoNeto, montoExento, montoIVA, total } =
    totalesDelDocumento(watchedLines as any, opcionesDeTotales)
  // Retención solo para tipo 46 (FC - Factura de Compra)
  const isFC = watchedDocType === ('FC' as string)

  // Auto-fill línea a partir del producto seleccionado
  const fillProductInLine = (index: number, product: ProductListItem) => {
    setValue(`lines.${index}.sku`, product.sku)
    setValue(`lines.${index}.name`, product.name)
    setValue(`lines.${index}.unit`, product.unit)
    setValue(`lines.${index}.unit_price`, product.unit_price)
    setValue(`lines.${index}.is_tax_exempt`, product.is_tax_exempt)
    const currency = (product as any).price_currency || baseCurrency
    setValue(`lines.${index}.price_currency` as any, currency)
  }

  // Items de autocompletado para productos (por SKU y por Nombre)


  const toggleDesc = (fieldId: string) => {
    setExpandedDesc(prev => {
      const next = new Set(prev)
      if (next.has(fieldId)) next.delete(fieldId)
      else next.add(fieldId)
      return next
    })
  }

  const toggleCC = (fieldId: string) => {
    setExpandedCC(prev => {
      const next = new Set(prev)
      if (next.has(fieldId)) next.delete(fieldId)
      else next.add(fieldId)
      return next
    })
  }

  // Merge global_campos into personalizados before submitting
  const handleFormSubmit = (data: DocumentFormData) => {
    const globalMerged = data.global_campos
      .filter(c => c.valor !== '' || c.is_required || c.tipo === 'boolean')
      .map(({ nombre, tipo, valor }) => ({ nombre, tipo, valor }))

    onSubmit({
      ...data,
      personalizados: [...globalMerged, ...data.personalizados],
    })
  }

  if (!open) return null

  return (
    <Modal open onClose={onClose} title={initial?.id ? 'Editar documento' : 'Nuevo documento de venta'} size="2xl">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* ── Sección: Encabezado ── */}
          <div>
            <p className={sectionTitleCls}>Encabezado</p>
            {/* Avisar acá si el tipo elegido no tiene folios, en vez de dejar
                que el rechazo aparezca al emitir. */}
            <FolioStatusNotice documentType={watchedDocType} className="mb-3" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <SelectField
                dense
                className="col-span-2"
                label="Tipo de documento"
                selectProps={register('document_type')}
              >
                {DOCUMENT_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </SelectField>
              <div>
                <label htmlFor="documentmodal-issue-date" className={labelCls}>Fecha emisión</label>
                <Controller name="issue_date" control={control} render={({ field }) => <DatePicker id="documentmodal-issue-date" value={field.value} onChange={field.onChange} onBlur={field.onBlur} />} />
              </div>
              <SelectField
                dense
                label="Forma de pago"
                selectProps={register('payment_method')}
              >
                <option value="contado">Contado</option>
                <option value="credito">Crédito</option>
              </SelectField>
              <TextField
                dense
                className="col-span-2 sm:col-span-4"
                label="Observaciones"
                multiline
                rows={2}
                placeholder="Notas internas opcionales…"
                inputProps={register('notes')}
              />
            </div>

            {/* Condiciones de pago (visible si crédito) */}
            {watchedPaymentMethod === 'credito' && (
              <div className="mt-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                <p className="text-xs font-semibold text-primary-hover mb-2">Plazo de crédito</p>
                <div className="flex items-center gap-2">
                  {[30, 60, 90].map(d => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setValue('payment_term_days', d)}
                      className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                        watchedPaymentTermDays === d
                          ? 'bg-primary text-on-primary border-primary'
                          : 'bg-surface text-content-muted border-divider hover:bg-soft'
                      }`}
                    >
                      {d} días
                    </button>
                  ))}
                  <input aria-label="Plazo de crédito en días"
                    type="number"
                    min="1"
                    max="365"
                    {...register('payment_term_days', { valueAsNumber: true })}
                    className="w-20 px-2.5 py-1.5 text-xs border border-input-border rounded-md bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200"
                    placeholder="Días"
                  />
                  <span className="text-xs text-content-muted">días</span>
                </div>
                {watchedIssueDate && watchedPaymentTermDays > 0 && (
                  <p className="text-xs text-primary mt-1.5">
                    Vencimiento: {(() => {
                      const d = new Date(watchedIssueDate + 'T12:00:00')
                      d.setDate(d.getDate() + (watchedPaymentTermDays || 0))
                      return d.toLocaleDateString('es-CL')
                    })()}
                  </p>
                )}
              </div>
            )}

            {/* Campos de cabecera globales */}
            {globalCampoFields.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-2">Campos de cabecera</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {globalCampoFields.map((field, idx) => {
                    const isRequired = field.is_required
                    const tipo = field.tipo
                    return (
                      <div key={field.id} className={tipo === 'boolean' ? 'col-span-2 flex items-center gap-2 pt-5' : 'col-span-2'}>
                        {tipo === 'boolean' ? (
                          <>
                            <Controller
                              name={`global_campos.${idx}.valor`}
                              control={control}
                              render={({ field: f }) => (
                                <input
                                  type="checkbox"
                                  aria-label={field.nombre}
                                  checked={f.value === 'true'}
                                  onChange={e => f.onChange(e.target.checked ? 'true' : 'false')}
                                  className="h-4 w-4 rounded border-divider-strong text-primary focus:ring-primary"
                                />
                              )}
                            />
                            <label className="text-xs text-content">
                              {field.nombre}
                              {isRequired && <span className="text-danger-solid ml-0.5">*</span>}
                            </label>
                            {!isRequired && (
                              <button type="button" onClick={() => removeGlobalCampo(idx)} className="p-1 text-content-subtle hover:text-danger-solid transition-colors flex-shrink-0" title="Quitar del formulario"><X size={13} /></button>
                            )}
                          </>
                        ) : (
                          <>
                            <label className={labelCls}>
                              {field.nombre}
                              {isRequired && <span className="text-danger-solid ml-0.5">*</span>}
                            </label>
                            <div className="flex items-center gap-1">
                              {tipo === 'select' ? (
                                <select aria-label={field.nombre}
                                  {...register(`global_campos.${idx}.valor`, {
                                    required: isRequired ? `${field.nombre} es obligatorio.` : false,
                                  })}
                                  className={inputCls}
                                >
                                  <option value="">Seleccione…</option>
                                  {(field.opciones || []).map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              ) : tipo === 'fecha' ? (
                                <Controller
                                  name={`global_campos.${idx}.valor`}
                                  control={control}
                                  rules={{ required: isRequired ? `${field.nombre} es obligatorio.` : false }}
                                  render={({ field: f }) => (
                                    <DatePicker aria-label={field.nombre}
                                      value={f.value}
                                      onChange={f.onChange}
                                      onBlur={f.onBlur}
                                      className="flex-1"
                                    />
                                  )}
                                />
                              ) : (
                                <input aria-label={field.nombre}
                                  type={tipo === 'numero' ? 'number' : 'text'}
                                  {...register(`global_campos.${idx}.valor`, {
                                    required: isRequired ? `${field.nombre} es obligatorio.` : false,
                                  })}
                                  className={inputCls}
                                />
                              )}
                              {!isRequired && (
                                <button
                                  type="button"
                                  onClick={() => removeGlobalCampo(idx)}
                                  className="p-1 text-content-subtle hover:text-danger-solid transition-colors flex-shrink-0"
                                  title="Quitar del formulario"
                                >
                                  <X size={13} />
                                </button>
                              )}
                            </div>
                            {errors.global_campos?.[idx]?.valor && (
                              <p className={errorCls}>{errors.global_campos[idx]?.valor?.message}</p>
                            )}
                          </>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Sección: Receptor ── */}
          <div>
            <p className={sectionTitleCls}>Receptor</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="col-span-2 sm:col-span-4">
                <label htmlFor="documentmodal-customer" className={labelCls}>
                  Cliente <span className="text-danger-solid">*</span>
                </label>
                <Controller
                  name="customer"
                  control={control}
                  rules={{ required: 'El cliente es obligatorio.' }}
                  render={({ field }) => {
                    const customerItems: AutocompleteItem[] = customers.map(c => ({
                      id: c.id,
                      label: c.legal_name,
                      sublabel: c.tax_id ? formatRut(c.tax_id) : undefined,
                    }))
                    const selectedCustomer = customers.find(c => c.id === field.value)
                    return (
                      <Autocomplete id="documentmodal-customer"
                        items={customerItems}
                        value={field.value}
                        displayValue={selectedCustomer ? `${selectedCustomer.legal_name}${selectedCustomer.tax_id ? ` (${formatRut(selectedCustomer.tax_id)})` : ''}` : ''}
                        onChange={field.onChange}
                        placeholder="Buscar cliente por nombre o RUT…"
                        className={inputCls}
                        required
                      />
                    )
                  }}
                />
                {errors.customer && <p className={errorCls}>{errors.customer.message}</p>}
              </div>

              {watchedCustomerId && (
                <>
                  <TextField
                    dense
                    label="RUT"
                    value={formatRut(receptorDisplay.tax_id)}
                    readOnly
                  />
                  <TextField
                    dense
                    label="Razón Social"
                    value={receptorDisplay.legal_name}
                    readOnly
                  />
                  <TextField
                    dense
                    className="col-span-2"
                    label="Giro Comercial"
                    value={receptorDisplay.giro}
                    readOnly
                  />
                  <TextField
                    dense
                    className="col-span-2"
                    label="Dirección"
                    value={receptorDisplay.address_street}
                    readOnly
                  />
                  <TextField
                    dense
                    label="Comuna"
                    value={receptorDisplay.address_commune}
                    readOnly
                  />
                  <TextField
                    dense
                    label="Ciudad"
                    value={receptorDisplay.address_city}
                    readOnly
                  />
                </>
              )}
            </div>
          </div>

          {/* ── Sección: Líneas ── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className={sectionTitleCls}>Líneas del documento</p>
              <button
                type="button"
                onClick={() => appendLine({ ...EMPTY_LINE })}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
              >
                <Plus size={12} />Agregar línea
              </button>
            </div>

            <div className="space-y-1.5">
              {lineFields.map((field, index) => {
                const linea = (watchedLines[index] ?? {}) as any
                const lineCurrency = linea.price_currency || baseCurrency
                const lineRate = getRate(lineCurrency)
                const lineTotal = subtotalDeLinea(linea, opcionesDeTotales)
                const descOpen = expandedDesc.has(field.id)
                const ccOpen = expandedCC.has(field.id)

                return (
                  <div
                    key={field.id}
                    className="p-2.5 bg-glass-3 rounded-lg border border-divider space-y-1.5"
                  >
                    {/* Fila principal: SKU | Nombre | Unidad | Qty | Precio | Delete */}
                    <div className="grid grid-cols-12 gap-1.5 items-end">
                      <div className="col-span-2">
                        <label className="block text-xs text-content-muted mb-0.5">SKU</label>
                        <Controller
                          name={`lines.${index}.sku`}
                          control={control}
                          render={({ field }) => (
                            <ProductAutocomplete aria-label="SKU"
                              field="sku"
                              value={field.value}
                              onChange={field.onChange}
                              onSelectProduct={(product) => { fillProductInLine(index, product) }}
                              placeholder="PROD-001"
                              className={inputCls}
                            />
                          )}
                        />
                      </div>
                      <div className="col-span-4">
                        <label className="block text-xs text-content-muted mb-0.5">
                          Nombre <span className="text-danger-solid">*</span>
                        </label>
                        <Controller
                          name={`lines.${index}.name`}
                          control={control}
                          rules={{ required: 'Requerido' }}
                          render={({ field }) => (
                            <ProductAutocomplete aria-label="Nombre"
                              field="name"
                              value={field.value}
                              onChange={field.onChange}
                              onSelectProduct={(product) => { fillProductInLine(index, product) }}
                              placeholder="Nombre del ítem"
                              className={inputCls}
                            />
                          )}
                        />
                        {errors.lines?.[index]?.name && (
                          <p className={errorCls}>{errors.lines[index]?.name?.message}</p>
                        )}
                      </div>
                      <SelectField
                        className="col-span-2"
                        label="Unidad"
                        selectProps={register(`lines.${index}.unit`)}
                      >
                        {UNITS.map(u => <option key={u} value={u}>{u}</option>)}

                      </SelectField>
                      <div className="col-span-1">
                        <label className="block text-xs text-content-muted mb-0.5">Qty</label>
                        <Controller
                          name={`lines.${index}.quantity`}
                          control={control}
                          rules={{ required: true, min: 0 }}
                          render={({ field }) => (
                            <NumericInput aria-label="Qty" {...field} locale={locale} decimals={3} min={0} className={inputCls} />
                          )}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs text-content-muted mb-0.5">
                          Precio neto
                          {lineCurrency !== baseCurrency && (
                            <span className="ml-1 px-1 py-0 text-[10px] font-bold bg-primary-100 text-primary-hover rounded">
                              {lineCurrency}
                            </span>
                          )}
                        </label>
                        <div className="space-y-0.5">
                          <Controller
                            name={`lines.${index}.unit_price`}
                            control={control}
                            rules={{ required: true, min: 0 }}
                            render={({ field }) => (
                              <NumericInput aria-label={`Precio unitario — línea ${index + 1}`} {...field} locale={locale} decimals={3} min={0} className={inputCls} />
                            )}
                          />
                          {lineCurrency !== baseCurrency && lineRate && (
                            <div className="text-[10px] text-content-muted pl-1">
                              = {formatMoneyForTenant(precioEnMonedaBase(linea, opcionesDeTotales), baseCurrency)}
                              <span className="text-primary ml-1">(1 {lineCurrency} = {formatMoneyForTenant(lineRate, baseCurrency)})</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-span-1 flex justify-center pb-0.5">
                        {lineFields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLine(index)}
                            className="p-1 text-danger-solid hover:text-danger-content hover:bg-danger-surface rounded transition-colors"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Fila secundaria: Exento | toggle desc | Descuento % | Subtotal */}
                    <div className="flex items-center gap-3 text-xs">
                      <label className="inline-flex items-center gap-1 text-content-muted cursor-pointer select-none">
                        <input
                          type="checkbox"
                          {...register(`lines.${index}.is_tax_exempt`)}
                          className="h-3 w-3 rounded border-divider-strong text-primary focus:ring-primary"
                        />
                        Exento
                      </label>
                      <span className="text-content-subtle">|</span>
                      <button
                        type="button"
                        onClick={() => toggleDesc(field.id)}
                        className="inline-flex items-center gap-0.5 text-primary hover:text-primary-hover transition-colors"
                      >
                        {descOpen
                          ? <ChevronDown size={11} />
                          : <ChevronRight size={11} />}
                        Descripción
                      </button>
                      <span className="text-content-subtle">|</span>
                      <button
                        type="button"
                        onClick={() => toggleCC(field.id)}
                        className={`inline-flex items-center gap-0.5 transition-colors ${
                          ccOpen || (watchedLines[index]?.cost_center_allocations?.length ?? 0) > 0
                            ? 'text-info-content-soft hover:text-info-content-strong dark:text-violet-200'
                            : 'text-primary hover:text-primary-hover'
                        }`}
                      >
                        <Building2 size={11} />
                        CC
                        {(watchedLines[index]?.cost_center_allocations?.length ?? 0) > 0 && (
                          <span className="text-[10px] bg-info-surface-strong text-info-content px-1 rounded">
                            {watchedLines[index]?.cost_center_allocations?.length}
                          </span>
                        )}
                      </button>
                      <span className="text-content-subtle">|</span>
                      <label className="text-content-muted flex items-center gap-1">
                        Desc. %
                        <input
                          type="number"
                          step="1"
                          min="0"
                          max="100"
                          {...register(`lines.${index}.discount_pct`)}
                          placeholder="0"
                          className="w-14 px-1.5 py-0.5 text-xs border border-divider rounded bg-surface focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </label>
                      <span className="ml-auto text-content-muted">
                        Subtotal:{' '}
                        <span className="font-semibold text-content">{formatCLP(lineTotal)}</span>
                      </span>
                    </div>

                    {/* Descripción (colapsable) */}
                    {descOpen && (
                      <div>
                        <input aria-label={`Detalle adicional — línea ${index + 1}`}
                          {...register(`lines.${index}.description`)}
                          placeholder="Detalle adicional del ítem (DscItem)…"
                          className={inputCls}
                        />
                      </div>
                    )}

                    {/* Centro de Costo (colapsable) */}
                    {ccOpen && (
                      <div className="bg-violet-50/50 border border-info-border rounded-md p-2">
                        <p className="text-[10px] text-info-solid mb-1 font-medium uppercase tracking-wider">
                          Centros de Costo
                        </p>
                        <CostCenterAllocator
                          allocations={watchedLines[index]?.cost_center_allocations ?? []}
                          onChange={(allocs: CostCenterAllocation[]) =>
                            setValue(`lines.${index}.cost_center_allocations`, allocs)
                          }
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Panel de totales desglosados */}
            <div className="mt-2 flex justify-end">
              <div className="w-64 bg-surface-muted border border-divider rounded-lg p-3 text-xs space-y-1">
                <div className="flex justify-between text-content-muted">
                  <span>Monto Neto</span>
                  <span className="font-medium">{formatCLP(montoNeto)}</span>
                </div>
                {montoExento > 0 && (
                  <div className="flex justify-between text-content-muted">
                    <span>Monto Exento</span>
                    <span>{formatCLP(montoExento)}</span>
                  </div>
                )}
                <div className="flex justify-between text-content-muted">
                  <span>{`${tax.name} (${taxRate}%)`}</span>
                  <span className="font-medium">{formatCLP(montoIVA)}</span>
                </div>
                {isFC && (
                  <div className="flex justify-between text-warning-content-soft">
                    <span>{`${tax.name} Retenido (${taxRate}%)`}</span>
                    <span>-{formatCLP(montoIVA)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-divider-strong pt-1.5 mt-1 text-content">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-sm">{formatCLP(isFC ? total - montoIVA : total)}</span>
                </div>
                {foreignCurrency && (lockedRates?.[foreignCurrency] || currencyRates?.[foreignCurrency]) && (
                  <div className="border-t border-divider pt-1.5 mt-1 space-y-0.5">
                    <div className="flex justify-between text-[10px] text-content-muted">
                      <span className="font-semibold text-primary">1 {foreignCurrency}</span>
                      <span>
                        ${parseFloat(String(lockedRates?.[foreignCurrency] ?? currencyRates![foreignCurrency].rate)).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {lockedRates?.[foreignCurrency]
                          ? ' (tasa del documento original)'
                          : ` (${currencyRates![foreignCurrency].rate_date})`}
                      </span>
                    </div>
                    <p className="text-[10px] text-warning-content-soft mt-0.5">
                      {lockedRates?.[foreignCurrency]
                        ? 'Se reusa la tasa del documento ajustado'
                        : 'Se recalcula con tasa vigente al emitir'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Sección: Referencias ── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className={sectionTitleCls}>Referencias</p>
              <button
                type="button"
                onClick={() => appendRef({ ...EMPTY_REFERENCIA })}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
              >
                <Plus size={12} />Agregar referencia
              </button>
            </div>
            {refFields.length === 0 ? (
              <p className="text-xs text-content-subtle italic">Sin referencias.</p>
            ) : (
              <div className="space-y-1.5">
                {refFields.map((field, idx) => (
                  <div
                    key={field.id}
                    className="p-2.5 bg-glass-3 rounded-lg border border-divider"
                  >
                    <div className="grid grid-cols-12 gap-1.5">
                      <TextField
                        className="col-span-2"
                        label="Tipo Doc"
                        placeholder="33"
                        inputProps={register(`referencias.${idx}.tipo_doc`, { required: true })}
                      />
                      <TextField
                        className="col-span-2"
                        label="Folio Ref"
                        placeholder="123"
                        inputProps={register(`referencias.${idx}.folio_ref`, { required: true })}
                      />
                      <div className="col-span-2">
                        <label className="block text-xs text-content-muted mb-0.5">Fecha Ref</label>
                        <Controller name={`referencias.${idx}.fecha_ref`} control={control} rules={{ required: true }} render={({ field }) => <DatePicker aria-label="Fecha Ref" value={field.value} onChange={field.onChange} onBlur={field.onBlur} required />} />
                      </div>
                      <SelectField
                        className="col-span-3"
                        label="Código"
                        selectProps={register(`referencias.${idx}.cod_ref`)}
                      >
                        {COD_REF_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                        ))}

                      </SelectField>
                      <TextField
                        className="col-span-2"
                        label="Razón"
                        placeholder="Corrige monto"
                        inputProps={register(`referencias.${idx}.razon_ref`)}
                      />
                      <div className="col-span-1 flex items-end justify-center pb-0.5">
                        <button
                          type="button"
                          onClick={() => removeRef(idx)}
                          className="p-1 text-danger-solid hover:text-danger-content hover:bg-danger-surface rounded transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Sección: Campos Personalizados ── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className={sectionTitleCls}>Campos Personalizados</p>
              <button
                type="button"
                onClick={() => appendPers({ ...EMPTY_PERSONALIZADO })}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
              >
                <Plus size={12} />Agregar campo
              </button>
            </div>
            {persFields.length === 0 ? (
              <p className="text-xs text-content-subtle italic">Sin campos personalizados.</p>
            ) : (
              <div className="space-y-1.5">
                {persFields.map((field, idx) => (
                  <div
                    key={field.id}
                    className="p-2.5 bg-glass-3 rounded-lg border border-divider"
                  >
                    <div className="grid grid-cols-12 gap-1.5">
                      <SelectField
                        className="col-span-3"
                        label="Tipo"
                        selectProps={register(`personalizados.${idx}.tipo`)}
                      >
                        <option value="string">Texto</option>
                        <option value="numero">Número</option>
                        <option value="fecha">Fecha</option>
                        <option value="select">Menú desplegable</option>
                        <option value="boolean">Casilla</option>

                      </SelectField>
                      <TextField
                        className="col-span-4"
                        label="Nombre"
                        placeholder="OrdenDeCompra"
                        inputProps={register(`personalizados.${idx}.nombre`, { required: true })}
                      />
                      <TextField
                        className="col-span-4"
                        label="Valor"
                        placeholder="OC-2025-001"
                        inputProps={register(`personalizados.${idx}.valor`)}
                      />
                      <div className="col-span-1 flex items-end justify-center pb-0.5">
                        <button
                          type="button"
                          onClick={() => removePers(idx)}
                          className="p-1 text-danger-solid hover:text-danger-content hover:bg-danger-surface rounded transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-5 py-3.5 border-t border-divider flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="inline-flex items-center gap-2 px-5 py-1.5 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors"
          >
            {isPending && <Loader2 size={13} className="animate-spin" />}
            {initial?.id ? 'Guardar cambios' : 'Crear documento'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
